import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import BottomNav from './components/BottomNav.jsx';
import EventForm from './components/EventForm.jsx';
import GestureOverlay from './components/GestureOverlay.jsx';
import HomePage from './components/HomePage.jsx';
import LoginScreen from './components/LoginScreen.jsx';
import SettingsForm from './components/SettingsForm.jsx';
import { DiaperPage, FeedPage, SleepPage } from './components/StatsPages.jsx';
import VaccinationForm from './components/VaccinationForm.jsx';
import VaccinePage from './components/VaccinePage.jsx';
import { clearSavedKey, createApi, loadSavedKey, saveKey } from './lib/api.js';
import { eventSummary } from './lib/format.js';

const EMPTY_DATA = {
  profile: { babyName: '宝宝', birthDate: null, sex: 'unknown', province: '', jeScheme: 'live', hepAScheme: 'live', quickBreastmilkInitial: 20, quickFormulaInitial: 20, quickNursingInitial: 1, quickFeedStep: 5, quickNursingStep: 1, quickHoldIntervalMs: 500 },
  events: [],
  activeSleep: null,
  vaccine: { version: '国家免疫规划疫苗儿童免疫程序（2026年版）', sourceUrl: '', schedule: [], records: [] },
};

export default function App() {
  const savedKey = useMemo(loadSavedKey, []);
  const [workspaceKey, setWorkspaceKey] = useState(savedKey);
  const [data, setData] = useState(EMPTY_DATA);
  const [activeTab, setActiveTab] = useState('home');
  const [range, setRange] = useState(7);
  const [now, setNow] = useState(new Date());
  const [busy, setBusy] = useState(false);
  const [loginBusy, setLoginBusy] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [gesture, setGesture] = useState(null);
  const [eventModal, setEventModal] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [vaccinationModal, setVaccinationModal] = useState(null);
  const [toast, setToast] = useState(null);
  const [syncState, setSyncState] = useState('idle');
  const toastTimer = useRef(null);
  const api = useMemo(() => workspaceKey ? createApi(workspaceKey) : null, [workspaceKey]);

  const refresh = useCallback(async ({ quiet = false } = {}) => {
    if (!api) return;
    setSyncState('loading');
    if (!quiet) setBusy(true);
    try {
      const next = await api.bootstrap();
      setData(next);
      setSyncState('ready');
    } catch (error) {
      setSyncState('error');
      if (!quiet) showToast(error.message, 'error');
    } finally {
      if (!quiet) setBusy(false);
    }
  }, [api]);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!api) return undefined;
    refresh();
    const poll = setInterval(() => refresh({ quiet: true }), 30000);
    function refreshOnFocus() {
      if (!document.hidden) refresh({ quiet: true });
    }
    document.addEventListener('visibilitychange', refreshOnFocus);
    return () => {
      clearInterval(poll);
      document.removeEventListener('visibilitychange', refreshOnFocus);
    };
  }, [api, refresh]);

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  function showToast(message, type = 'success', undo = null) {
    clearTimeout(toastTimer.current);
    setToast({ message, type, undo });
    toastTimer.current = setTimeout(() => setToast(null), undo ? 6000 : 3500);
  }

  async function enterWorkspace(key, remember) {
    setLoginBusy(true);
    setSyncState('loading');
    setLoginError('');
    try {
      const initial = await createApi(key).bootstrap();
      saveKey(key, remember);
      setData(initial);
      setWorkspaceKey(key);
      setSyncState('ready');
    } catch (error) {
      setSyncState('error');
      setLoginError(error.message);
    } finally {
      setLoginBusy(false);
    }
  }

  function logout() {
    clearSavedKey();
    setWorkspaceKey('');
    setData(EMPTY_DATA);
    setSettingsOpen(false);
    setActiveTab('home');
  }

  async function createEvent(payload, label) {
    setBusy(true);
    try {
      const created = await api.createEvent(payload);
      setData((current) => ({
        ...current,
        events: [created, ...current.events.filter((event) => event.id !== created.id)],
        activeSleep: created.kind === 'sleep' && !created.endedAt ? created : current.activeSleep,
      }));
      setEventModal(null);
      showToast(label ?? `已记录${eventSummary(created)}`, 'success', async () => {
        try {
          await api.deleteEvent(created.id);
          await refresh({ quiet: true });
          showToast('已撤销记录');
        } catch (error) {
          showToast(error.message, 'error');
        }
      });
      return created;
    } finally {
      setBusy(false);
    }
  }

  async function handleGestureComplete(result) {
    try {
      const startedAt = new Date().toISOString();
      if (result.kind === 'feed') {
        const nursing = result.selected === 'nursing';
        const payload = { kind: 'feed', subtype: result.selected, amount: result.amount, startedAt };
        if (nursing) payload.side = result.side;
        await createEvent(payload, nursing
          ? `已记录亲喂 ${result.amount} 分钟 · ${result.side === 'left' ? '左侧' : '右侧'}`
          : `已记录${result.selected === 'breastmilk' ? '母乳瓶喂' : '奶粉瓶喂'} ${result.amount} ml`);
      } else {
        await createEvent({ kind: result.kind, amount: result.amount, startedAt }, `已记录${result.kind === 'poop' ? '臭臭' : '小便'}`);
      }
    } catch (error) {
      showToast(error.message, 'error');
      await refresh({ quiet: true });
    }
  }

  async function toggleSleep() {
    setBusy(true);
    try {
      if (data.activeSleep) {
        const stopped = await api.stopSleep(new Date().toISOString());
        setData((current) => ({
          ...current,
          activeSleep: null,
          events: current.events.map((event) => event.id === stopped.id ? stopped : event),
        }));
        showToast('睡眠已结束');
      } else {
        const created = await api.createEvent({ kind: 'sleep', startedAt: new Date().toISOString() });
        setData((current) => ({ ...current, activeSleep: created, events: [created, ...current.events] }));
        showToast('睡眠计时已开始', 'success', async () => {
          try {
            await api.deleteEvent(created.id);
            await refresh({ quiet: true });
            showToast('已撤销睡眠计时');
          } catch (error) {
            showToast(error.message, 'error');
          }
        });
      }
    } catch (error) {
      showToast(error.message, 'error');
      await refresh({ quiet: true });
    } finally {
      setBusy(false);
    }
  }

  async function saveEvent(payload) {
    if (!eventModal?.event) return createEvent(payload);
    setBusy(true);
    try {
      const updated = await api.updateEvent(eventModal.event.id, payload);
      setData((current) => ({
        ...current,
        events: current.events.map((event) => event.id === updated.id ? updated : event).sort((a, b) => new Date(b.startedAt) - new Date(a.startedAt)),
        activeSleep: updated.kind === 'sleep' && !updated.endedAt ? updated : current.activeSleep?.id === updated.id ? null : current.activeSleep,
      }));
      setEventModal(null);
      showToast('记录已更新');
    } finally {
      setBusy(false);
    }
  }

  async function deleteEvent(event = eventModal?.event) {
    if (!event || !window.confirm(`删除“${eventSummary(event)}”？`)) return;
    setBusy(true);
    try {
      await api.deleteEvent(event.id);
      setData((current) => ({
        ...current,
        events: current.events.filter((item) => item.id !== event.id),
        activeSleep: current.activeSleep?.id === event.id ? null : current.activeSleep,
      }));
      setEventModal(null);
      showToast('记录已删除');
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setBusy(false);
    }
  }

  async function saveProfile(profile) {
    setBusy(true);
    try {
      await api.updateProfile(profile);
      await refresh({ quiet: true });
      setSettingsOpen(false);
      showToast('宝宝资料已保存');
    } finally {
      setBusy(false);
    }
  }

  function openDose(item) {
    setVaccinationModal({ item, record: item.record, optional: false, scheduleId: item.id });
  }

  function openOptional(record = null) {
    setVaccinationModal({
      item: null,
      record,
      optional: true,
      scheduleId: record?.scheduleId ?? `custom-${Date.now().toString(36)}`,
    });
  }

  async function saveVaccination(record) {
    setBusy(true);
    try {
      await api.saveVaccination(vaccinationModal.scheduleId, record);
      await refresh({ quiet: true });
      setVaccinationModal(null);
      showToast('接种记录已保存');
    } finally {
      setBusy(false);
    }
  }

  async function deleteVaccination() {
    if (!vaccinationModal?.record || !window.confirm('删除这条接种记录？')) return;
    setBusy(true);
    try {
      await api.deleteVaccination(vaccinationModal.scheduleId);
      await refresh({ quiet: true });
      setVaccinationModal(null);
      showToast('接种记录已删除');
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setBusy(false);
    }
  }

  function exportData() {
    const payload = {
      exportedAt: new Date().toISOString(),
      profile: data.profile,
      events: data.events,
      vaccine: data.vaccine,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${data.profile.babyName || '宝宝'}-喂养记录-${new Date().toISOString().slice(0, 10)}.json`;
    link.click();
    URL.revokeObjectURL(link.href);
    showToast('数据已导出');
  }

  if (!workspaceKey) {
    return <LoginScreen initialKey={savedKey} onEnter={enterWorkspace} busy={loginBusy} error={loginError} />;
  }

  let page;
  const sharedTimelineActions = {
    onEdit: (event) => setEventModal({ kind: event.kind, event }),
    onDelete: deleteEvent,
  };
  if (activeTab === 'home') {
    page = <HomePage profile={data.profile} events={data.events} activeSleep={data.activeSleep} now={now} gesture={gesture} onGestureChange={setGesture} onGestureComplete={handleGestureComplete} onOpenForm={(kind) => setEventModal({ kind })} onToggleSleep={toggleSleep} onOpenSettings={() => setSettingsOpen(true)} busy={busy} {...sharedTimelineActions} />;
  } else if (activeTab === 'feed') {
    page = <FeedPage events={data.events} range={range} onRangeChange={setRange} onAdd={(kind) => setEventModal({ kind })} now={now} {...sharedTimelineActions} />;
  } else if (activeTab === 'diaper') {
    page = <DiaperPage events={data.events} range={range} onRangeChange={setRange} onAdd={(kind) => setEventModal({ kind })} now={now} {...sharedTimelineActions} />;
  } else if (activeTab === 'sleep') {
    page = <SleepPage events={data.events} activeSleep={data.activeSleep} range={range} onRangeChange={setRange} onAdd={(kind) => setEventModal({ kind })} onToggleSleep={toggleSleep} now={now} {...sharedTimelineActions} />;
  } else {
    page = <VaccinePage profile={data.profile} vaccine={data.vaccine} onOpenProfile={() => setSettingsOpen(true)} onOpenDose={openDose} onOpenOptional={openOptional} />;
  }

  return (
    <div className={`app-shell ${toast ? 'has-toast' : ''}`}>
      {syncState === 'loading' ? <div className="sync-banner sync-banner--loading" role="status">正在同步家庭记录…</div> : null}
      {syncState === 'error' ? <div className="sync-banner sync-banner--error" role="alert"><span>同步失败，当前可能不是最新数据</span><button type="button" onClick={() => refresh()}>重新加载</button></div> : null}
      <main className="app-main">{page}</main>
      <BottomNav active={activeTab} onChange={setActiveTab} />
      <GestureOverlay gesture={gesture} />
      {eventModal ? <EventForm kind={eventModal.kind} event={eventModal.event} profile={data.profile} onClose={() => setEventModal(null)} onSave={saveEvent} onDelete={eventModal.event ? () => deleteEvent(eventModal.event) : undefined} busy={busy} /> : null}
      {settingsOpen ? <SettingsForm profile={data.profile} onClose={() => setSettingsOpen(false)} onSave={saveProfile} onExport={exportData} onLogout={logout} busy={busy} /> : null}
      {vaccinationModal ? <VaccinationForm item={vaccinationModal.item} record={vaccinationModal.record} optional={vaccinationModal.optional} onClose={() => setVaccinationModal(null)} onSave={saveVaccination} onDelete={vaccinationModal.record ? deleteVaccination : undefined} busy={busy} /> : null}
      {toast ? (
        <div className={`toast toast--${toast.type}`} role="status">
          <span>{toast.message}</span>
          {toast.undo ? <button type="button" onClick={() => { const undo = toast.undo; setToast(null); undo(); }}>撤销</button> : null}
        </div>
      ) : null}
    </div>
  );
}
